import CodeBlockEditor from "../../../CodeBlockEditor/CodeBlockEditor"
import Node from "rich-markdown-editor/dist/nodes/Node"
import Keyboard from "./Keyboard/Keyboard"
import { Node as ProseMirrorNode, NodeType, Schema } from "prosemirror-model"
import { EditorState, NodeSelection, Transaction } from "prosemirror-state"
import { MarkdownButtonBlock } from "../../../../models/markdownButton"
import {
    dataToParamString,
    defaultButtonsParameters,
    getKeyboard,
    getLanguageWithParameters,
    isCommaUsedToSeparate,
    isQuotesRequired,
    keyboardToString,
    markdownButtonDefault
} from "../../../../utility/articleContentEditor/markdownButtons"
import { MarkdownButton } from "../../../../utility/articleContentEditor/buttonsParser/buttonsParser"
import { textblockTypeInputRule } from "prosemirror-inputrules"
import NextAnswer from "../../../NextAnswer/NextAnswer"
import QuickActions from "../QuickActions/QuickActions/QuickActions"
import WidgetBlock from "../Widget/Widget/WidgetBlock"
import { defaultWidget, Widget } from "../Widget/MarkdownWidgetForm/MarkdownWidgetForm"
import { MarkdownCustomLanguages } from "../../../../utility/articleContentEditor/markdownCustomLanguages"
import { v4 as uuidV4 } from "uuid"
import { getDefaultQuickActions, getQuickActions } from "../../../../utility/articleContentEditor/markdownQuickActions"
import { TFunction } from "react-i18next"
import { MarkdownSerializerState } from "prosemirror-markdown"
import WebchatCard, { DebugWebchat, defaultDebugWebchat } from "../Webchat/WebchatCard/WebchatCard"
import WebchatFrame from "../Webchat/WebchatFrame/WebchatFrame"
import Comparison from "../Comparison/Comparison"
import { Sticker } from "../Sticker/Sticker"
import { findParentNode } from "prosemirror-utils"
import { Provider as ReduxProvider } from "react-redux"
import { ReactElement } from "react"
import { Store } from "../../../../store/store"

const defaultLanguage = ""

const markdownLanguages: string[] = [
    MarkdownCustomLanguages.QuickActions,
    MarkdownCustomLanguages.Widget,
    MarkdownCustomLanguages.DebugWebchat,
    MarkdownCustomLanguages.BlockComparison
]
const isMarkdownLanguage = (language: string) => markdownLanguages.includes(language)

export enum CodeFenceNodeMeta {
    Buttons = "buttons",
    Code = "code"
}

interface CommandOptions {
    schema: Schema
    type: NodeType
}

const isStickerCodeFenceNode = (node: ProseMirrorNode): boolean => {
    return node.type.name === "code_fence" && node.attrs.language === "sticker"
}

const safeFindStickerFence = (state: EditorState) => {
    const { selection } = state

    const found = findParentNode(node => isStickerCodeFenceNode(node))(selection)

    if (found) return found

    const { $from } = selection

    const parent = $from.node($from.depth - 1)
    const index = $from.index($from.depth - 1)

    if (index > 0) {
        const previousNode = parent.child(index - 1)

        if (isStickerCodeFenceNode(previousNode)) {
            let position = $from.start($from.depth - 1)
            for (let i = 0; i < index - 1; i++) {
                position += parent.child(i).nodeSize
            }
            return {
                pos: position,
                node: previousNode
            }
        }
    }

    return null
}

export const getDefaultParameters = (language: string) => {
    switch (language) {
        case MarkdownCustomLanguages.Buttons:
            return defaultButtonsParameters
        case MarkdownCustomLanguages.Widget:
            return {
                id: uuidV4(),
                title: "",
                description: ""
            }
        case MarkdownCustomLanguages.DebugWebchat:
            return defaultDebugWebchat
        default:
            return {}
    }
}

const getDefaultMarkdown = (language: string, t: TFunction) => {
    switch (language) {
        case MarkdownCustomLanguages.QuickActions:
            return getDefaultQuickActions(t)
        case MarkdownCustomLanguages.Sticker:
            return "```sticker(id:)```"
        case MarkdownCustomLanguages.DebugWebchat:
        case MarkdownCustomLanguages.Widget:
        case MarkdownCustomLanguages.BlockComparison:
            return ""
        default:
            return {}
    }
}

class KeyboardNode extends Node {
    get name() {
        return "code_fence"
    }

    get schema() {
        return {
            attrs: {
                language: {
                    default: defaultLanguage
                },
                parameters: {
                    default: {}
                },
                keyboard: {
                    default: {}
                },
                markdown: {
                    default: ""
                }
            },
            content: "text*",
            marks: "",
            group: "block",
            code: true,
            defining: true,
            draggable: false,
            parseDOM: [
                {
                    tag: "pre",
                    preserveWhitespace: "full",
                    getAttrs: (dom: HTMLDivElement) => ({
                        ...defaultAttrs,
                        language: dom.dataset.language ?? defaultLanguage,
                        initial: true
                    })
                },
                {
                    tag: ".code-block",
                    preserveWhitespace: "full",
                    contentElement: "code",
                    getAttrs: (dom: HTMLDivElement) => ({
                        ...defaultAttrs,
                        language: dom.dataset.language ?? defaultLanguage,
                        initial: true
                    })
                }
            ],
            toDOM: () => {
                return ["div", ["pre", ["code", { spellCheck: false }, 0]]]
            }
        }
    }

    commands(options: CommandOptions) {
        const { type } = options
        return (attrs: { language: string }) => {
            return (state: EditorState, dispatch: (tr: Transaction) => void) => {
                const { language } = attrs
                const tr = state.tr
                    .replaceSelectionWith(
                        type.create({
                            language,
                            parameters: getDefaultParameters(language),
                            keyboard:
                                language === MarkdownCustomLanguages.Buttons
                                    ? { "1": [{ ...markdownButtonDefault(this.options.t) }] }
                                    : {},
                            markdown: getDefaultMarkdown(language, this.options.t),
                            initial: true
                        })
                    )
                    .scrollIntoView()
                dispatch(tr)
            }
        }
    }

    handleUpdateButtons = (keyboard: { [rowIdx: number]: MarkdownButton[] }, block: MarkdownButtonBlock): void => {
        const { view } = this.editor
        const { state, dispatch } = view

        dispatch(
            state.tr
                .replaceSelectionWith(
                    state.schema.nodes.code_fence.create({
                        ...defaultAttrs,
                        language: MarkdownCustomLanguages.Buttons,
                        parameters: block,
                        keyboard
                    })
                )
                .scrollIntoView()
        )
    }

    handleRemoveNode = () => {
        const { view } = this.editor
        const { state, dispatch } = view
        dispatch(state.tr.deleteSelection().scrollIntoView())
    }

    handleSelect = (props: { node: ProseMirrorNode; isEditable: boolean; getPos: () => number }) => () => {
        const {
            view: { state, dispatch }
        } = this.editor
        const $pos = state.doc.resolve(props.getPos())
        // @ts-ignore
        const transaction = state.tr.setSelection(new NodeSelection($pos))
        dispatch(transaction)
    }

    handleCodeChanged = (code: string) => {
        const {
            view: { dispatch, state }
        } = this.editor

        const node = state.schema.nodes.code_fence.createAndFill(defaultAttrs, state.schema.text(code))

        if (node) {
            dispatch(
                state.tr.replaceSelectionWith(node).setMeta("codeFenceSubType", CodeFenceNodeMeta.Code).scrollIntoView()
            )
        }
    }

    handleWidgetChanged = (widget: Widget) => {
        const {
            view: { dispatch, state }
        } = this.editor

        dispatch(
            state.tr
                .replaceSelectionWith(
                    state.schema.nodes.code_fence.create({
                        ...defaultAttrs,
                        language: MarkdownCustomLanguages.Widget,
                        parameters: { id: widget.id, title: widget.title, description: widget.description },
                        markdown: widget.json
                    })
                )
                .scrollIntoView()
        )
    }

    handleStickerChanged = (id?: string) => {
        const {
            view: { dispatch, state }
        } = this.editor
        const { tr } = state

        const parentStickerNode = safeFindStickerFence(state)

        if (parentStickerNode) {
            // @ts-ignore
            const transaction = (tr as never).delete(
                parentStickerNode.pos,
                parentStickerNode.pos + parentStickerNode.node.nodeSize
            )

            if (id) {
                transaction.insert(
                    parentStickerNode.pos,
                    state.schema.nodes.code_fence.create({
                        ...defaultAttrs,
                        language: MarkdownCustomLanguages.Sticker,
                        parameters: { id },
                        markdown: `\`\`\`sticker(id:${id})\`\`\``
                    })
                )
            }

            dispatch(transaction.scrollIntoView())
        }
    }

    handleDebugWebchatChanged = (debugWebchat: DebugWebchat) => {
        const {
            view: { dispatch, state }
        } = this.editor
        dispatch(
            state.tr
                .replaceSelectionWith(
                    state.schema.nodes.code_fence.create({
                        ...defaultAttrs,
                        language: MarkdownCustomLanguages.DebugWebchat,
                        parameters: {
                            articleId: debugWebchat.articleId,
                            showInput: String(debugWebchat.showInput),
                            channelId: debugWebchat.channelId
                        }
                    })
                )
                .scrollIntoView()
        )
    }

    component = (props: { node: ProseMirrorNode; isEditable: boolean; isSelected: boolean; getPos: () => number }) => {
        const {
            node: {
                attrs: { language, keyboard, parameters, initial, markdown },
                textContent
            },
            isEditable,
            isSelected
        } = props
        const webchatContainer = {
            card: (
                <WebchatCard
                    debugWebchat={{ ...defaultDebugWebchat, ...parameters }}
                    projectId={this.options.projectId}
                    dispatch={this.options.dispatch}
                    onSelect={this.handleSelect(props)}
                    onChange={this.handleDebugWebchatChanged}
                    onDelete={this.handleRemoveNode}
                    channels={this.options.channels}
                    articleSymbolCode={this.options.articleSymbolCode}
                />
            ),
            frame: (
                <WebchatFrame
                    debugWebchat={{ ...defaultDebugWebchat, ...parameters }}
                    projectId={this.options.projectId}
                />
            )
        }

        let element: ReactElement | null = null

        switch (language) {
            case MarkdownCustomLanguages.Buttons:
                element = (
                    <Keyboard
                        projectId={this.options.projectId}
                        isEditable={isEditable}
                        buttonRows={keyboard}
                        block={parameters}
                        handleUpdateKeyboard={this.handleUpdateButtons}
                        onDeleteKeyboard={this.handleRemoveNode}
                        handleSelect={this.handleSelect(props)}
                        handleOpenArticle={this.options.onOpenArticle}
                        dispatch={this.options.dispatch}
                    />
                )
                break
            case MarkdownCustomLanguages.NextAnswer:
                element = <NextAnswer isSelected={isSelected} onSelect={this.handleSelect(props)} />
                break
            case MarkdownCustomLanguages.Widget:
                element = (
                    <WidgetBlock
                        widget={{ ...defaultWidget, ...parameters, json: markdown }}
                        isEditable={isEditable}
                        onSelect={this.handleSelect(props)}
                        onChange={this.handleWidgetChanged}
                        onDelete={this.handleRemoveNode}
                    />
                )
                break
            case MarkdownCustomLanguages.QuickActions:
                element = (
                    <QuickActions
                        onClick={this.handleSelect(props)}
                        actions={getQuickActions(markdown)}
                        isEditable={isEditable}
                        onLocationChange={this.options.onChangeLocation}
                    />
                )
                break
            case MarkdownCustomLanguages.DebugWebchat:
                element = isEditable ? webchatContainer.card : webchatContainer.frame
                break
            case MarkdownCustomLanguages.Sticker:
                const { id } = parameters

                element = (
                    <Sticker
                        id={id}
                        isEditable={isEditable}
                        onChange={this.handleStickerChanged}
                        stickers={this.options.stickers}
                    />
                )
                break
            case MarkdownCustomLanguages.BlockComparison:
                const { type, openOffset, closeOffset, closeColor } = parameters
                element = (
                    <Comparison type={type} openOffset={openOffset} closeOffset={closeOffset} closeColor={closeColor} />
                )
                break
            default:
                element = (
                    <CodeBlockEditor
                        initialContent={textContent}
                        initial={initial}
                        isEditable={isEditable}
                        onSelect={this.handleSelect(props)}
                        onChange={this.handleCodeChanged}
                        onRemove={this.handleRemoveNode}
                    />
                )
        }

        return this.withReduxProvider(element)
    }

    // @ts-ignore
    toMarkdown(state: MarkdownSerializerState, node: ProseMirrorNode) {
        const { parameters, language, keyboard, markdown } = node.attrs
        const params = dataToParamString(parameters, {
            separateByComma: isCommaUsedToSeparate(language),
            putInQuotes: isQuotesRequired(language)
        })
        state.write(`\`\`\`${language || ""}${params}\n`)

        if (language === MarkdownCustomLanguages.Buttons) {
            state.text(keyboardToString(keyboard), false)
        } else if (isMarkdownLanguage(language)) {
            state.text(markdown, false)
        } else {
            state.text(node.textContent, false)
        }

        state.ensureNewLine()
        state.write("```")
        state.closeBlock(node)
    }

    get markdownToken() {
        return "fence"
    }

    inputRules({ type }: { type: NodeType }) {
        return [
            textblockTypeInputRule(/^```$/, type, () => {
                return {
                    language: defaultLanguage,
                    parameters: {},
                    keyboard: {},
                    markdown: "",
                    initial: true
                }
            })
        ]
    }

    parseMarkdown() {
        return {
            block: "code_fence",
            getAttrs: (tok: Record<string, string>) => {
                const { language, parameters } = getLanguageWithParameters(tok.info)
                return {
                    ...defaultAttrs,
                    language,
                    parameters: parameters ?? {},
                    keyboard: language === MarkdownCustomLanguages.Buttons ? getKeyboard(tok.content) : {},
                    markdown: isMarkdownLanguage(language) ? tok.content : ""
                }
            }
        }
    }

    private withReduxProvider = (element: ReactElement | null) => {
        const store = this.options.store as Store | undefined

        if (!store || !element) {
            return element
        }

        return <ReduxProvider store={store}>{element}</ReduxProvider>
    }
}

const defaultAttrs = { language: defaultLanguage, parameters: {}, keyboard: {}, markdown: "", initial: false }

export default KeyboardNode

export class CodeBlock extends KeyboardNode {
    get name() {
        return "code_block"
    }

    get markdownToken() {
        return "code_block"
    }
}

export class MarkdownNode extends KeyboardNode {
    get name() {
        return MarkdownCustomLanguages.Markdown
    }

    get markdownToken() {
        return MarkdownCustomLanguages.Markdown
    }
}

export class QuickActionsNode extends KeyboardNode {
    get name() {
        return MarkdownCustomLanguages.QuickActions
    }

    get markdownToken() {
        return MarkdownCustomLanguages.QuickActions
    }
}

export class StickerNode extends KeyboardNode {
    get name() {
        return MarkdownCustomLanguages.Sticker
    }

    get markdownToken() {
        return MarkdownCustomLanguages.Sticker
    }
}
